import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

let toastSequence = 0;

// crypto.randomUUID() only exists in secure contexts (HTTPS or localhost) —
// a PM2 deployment reached over plain HTTP has no crypto.randomUUID, which
// throws and silently aborts the toast call (and whatever cleanup code was
// meant to run right after it, e.g. closing a modal), so this must not
// assume it's available.
function generateToastId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  toastSequence += 1;
  return `toast-${Date.now().toString(36)}-${toastSequence}`;
}

export enum ToastType {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  /** How long the toast stays up, in ms (the progress bar runs for this long). */
  duration: number;
  /** Set while the toast plays its exit animation, just before it is removed. */
  leaving?: boolean;
}

/** Errors stay longer so they can be read. */
const DURATION: Record<ToastType, number> = {
  [ToastType.Success]: 4000,
  [ToastType.Info]: 4000,
  [ToastType.Warning]: 5000,
  [ToastType.Error]: 6000,
};
const EXIT_ANIMATION_MS = 180;

interface ToastTimer {
  handle: ReturnType<typeof setTimeout> | null;
  startedAt: number;
  remaining: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toastsSubject = new BehaviorSubject<Toast[]>([]);
  toasts$ = this.toastsSubject.asObservable();
  private readonly timers = new Map<string, ToastTimer>();

  show(type: ToastType, message: string, title?: string): void {
    const id = generateToastId();
    const duration = DURATION[type];
    this.toastsSubject.next([...this.toastsSubject.value, { id, type, message, title, duration }]);
    this.timers.set(id, { handle: null, startedAt: 0, remaining: duration });
    this.resume(id);
  }

  success(message: string, title?: string): void {
    this.show(ToastType.Success, message, title);
  }
  error(message: string, title?: string): void {
    this.show(ToastType.Error, message, title);
  }
  warning(message: string, title?: string): void {
    this.show(ToastType.Warning, message, title);
  }
  info(message: string, title?: string): void {
    this.show(ToastType.Info, message, title);
  }

  /** Stops the auto-dismiss countdown (while the pointer is over the toast). */
  pause(id: string): void {
    const timer = this.timers.get(id);
    if (!timer?.handle) return;
    clearTimeout(timer.handle);
    timer.handle = null;
    timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
  }

  /** Restarts the countdown with the time that was left. */
  resume(id: string): void {
    const timer = this.timers.get(id);
    if (!timer || timer.handle) return;
    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => this.dismiss(id), timer.remaining);
  }

  dismiss(id: string): void {
    const timer = this.timers.get(id);
    if (timer?.handle) clearTimeout(timer.handle);
    this.timers.delete(id);
    const current = this.toastsSubject.value;
    if (!current.some(t => t.id === id && !t.leaving)) return;
    this.toastsSubject.next(current.map(t => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      this.toastsSubject.next(this.toastsSubject.value.filter(t => t.id !== id));
    }, EXIT_ANIMATION_MS);
  }
}
